import { ReactNode, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import NotificationManager from '@/services/NotificationManager';

/**
 * NotificationProvider
 * 
 * Ensures a SINGLE realtime connection is initialized once per session at app root.
 * - WebSockets handle realtime in web builds
 * - Polling is only a safety fallback (centralized in NotificationManager)
 * - On logout, we cleanup all channels/timers to avoid leaks
 */
export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  useEffect(() => {
    // Guard against rapid re-initialization
    let isActive = true;
    
    if (user?.id) {
      const manager = NotificationManager.getInstance();
      
      // Only initialize if still active after microtask
      Promise.resolve().then(() => {
        if (isActive) {
          try {
            // Initialize realtime/polling (idempotent, guarded in manager)
            manager.initialize(user.id);
            
            // Register native push notifications (only on native platforms)
            manager.registerPushHandler();
          } catch (error) {
            console.error('❌ NotificationProvider: Error during initialization:', error);
          }
        }
      });
      
      // Only cleanup when user changes or component unmounts
      return () => {
        isActive = false;
        try {
          manager.cleanup();
        } catch (error) {
          console.error('❌ NotificationProvider: Error during cleanup:', error);
        }
      };
    } else {
      // If user not present, ensure manager is cleaned up
      try {
        NotificationManager.getInstance().cleanup();
      } catch (error) {
        console.error('❌ NotificationProvider: Error during cleanup:', error);
      }
    }
  }, [user?.id]);

  return children as any;
};
